/**
 * DNA WhatsApp AI - Atendimento Automático
 * Fase 6 do DNA Growth Engine
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { 
  MessageCircle, Bot, Users, Send, Clock, User, 
  Settings, Plus, AlertCircle, CheckCircle, Loader2,
  Phone, Zap, RefreshCw, Sparkles
} from 'lucide-react';

const API_URL = window.location.origin || '';

const STATUS_CONVERSA = {
  aberta: { label: 'Aberta', color: 'bg-gray-500' },
  ia: { label: 'IA', color: 'bg-purple-500' },
  humano: { label: 'Aguardando Humano', color: 'bg-yellow-500' },
  finalizada: { label: 'Finalizada', color: 'bg-green-500' }
};

export default function DnaWhatsAppAI() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [conversas, setConversas] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [config, setConfig] = useState(null);
  const [integracaoStatus, setIntegracaoStatus] = useState(null);
  
  // Conversa selecionada
  const [conversaSelecionada, setConversaSelecionada] = useState(null);
  const [mensagemResposta, setMensagemResposta] = useState('');
  
  // Novo Template
  const [showNovoTemplate, setShowNovoTemplate] = useState(false);
  const [novoTemplate, setNovoTemplate] = useState({
    nome: '', categoria: 'saudacao', conteudo: ''
  });
  
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  };
  
  const carregarDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/growth/whatsapp/dashboard`, { headers: getAuthHeaders() });
      const data = await res.json();
      setDashboard(data);
    } catch (error) {
      console.error('Erro:', error);
    }
  }, []);
  
  const carregarConversas = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/growth/whatsapp/conversas`, { headers: getAuthHeaders() });
      const data = await res.json();
      setConversas(data.conversas || []);
    } catch (error) {
      console.error('Erro:', error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  const carregarTemplates = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/growth/whatsapp/templates`, { headers: getAuthHeaders() });
      const data = await res.json();
      setTemplates(data.templates || []);
    } catch (error) {
      console.error('Erro:', error);
    }
  }, []);
  
  const carregarConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/growth/whatsapp/config`, { headers: getAuthHeaders() });
      const data = await res.json();
      setConfig(data);
    } catch (error) {
      console.error('Erro:', error);
    }
  }, []);
  
  const verificarIntegracao = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/growth/whatsapp/integracao/status`, { headers: getAuthHeaders() });
      const data = await res.json();
      setIntegracaoStatus(data);
    } catch (error) {
      console.error('Erro:', error);
    }
  }, []);
  
  useEffect(() => {
    carregarDashboard();
    verificarIntegracao();
  }, [carregarDashboard, verificarIntegracao]);
  
  useEffect(() => {
    if (activeTab === 'conversas') carregarConversas();
    if (activeTab === 'templates') carregarTemplates();
    if (activeTab === 'config') carregarConfig();
  }, [activeTab, carregarConversas, carregarTemplates, carregarConfig]);
  
  const enviarMensagem = async () => {
    if (!conversaSelecionada || !mensagemResposta.trim()) return;
    
    try {
      const res = await fetch(`${API_URL}/api/growth/whatsapp/enviar`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          telefone: conversaSelecionada.telefone,
          mensagem: mensagemResposta
        })
      });
      
      if (res.ok) {
        toast.success('Mensagem enviada!');
        setMensagemResposta('');
        carregarConversas();
      }
    } catch (error) {
      toast.error('Erro ao enviar mensagem');
    }
  };
  
  const transferirParaHumano = async (conversaId) => {
    try {
      await fetch(`${API_URL}/api/growth/whatsapp/conversas/${conversaId}/transferir-humano`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      toast.success('Conversa transferida para atendimento humano');
      carregarConversas();
      carregarDashboard();
    } catch (error) {
      toast.error('Erro ao transferir');
    }
  };
  
  const criarTemplate = async () => {
    if (!novoTemplate.nome || !novoTemplate.conteudo) {
      toast.error('Preencha todos os campos');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/growth/whatsapp/templates`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(novoTemplate)
      });
      
      if (res.ok) {
        toast.success('Template criado!');
        setShowNovoTemplate(false);
        setNovoTemplate({ nome: '', categoria: 'saudacao', conteudo: '' });
        carregarTemplates();
      }
    } catch (error) {
      toast.error('Erro ao criar template');
    }
  };
  
  const salvarConfig = async () => {
    try {
      await fetch(`${API_URL}/api/growth/whatsapp/config`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(config)
      });
      toast.success('Configurações salvas!');
    } catch (error) {
      toast.error('Erro ao salvar');
    }
  };
  
  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <MessageCircle className="w-8 h-8 text-green-500" />
            DNA WhatsApp AI
          </h1>
          <p className="text-gray-500 mt-1">Atendimento Automático Inteligente</p>
        </div>
        
        {/* Status Integração */}
        <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
          integracaoStatus?.status === 'conectado' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
        }`}>
          {integracaoStatus?.status === 'conectado' ? (
            <CheckCircle className="w-4 h-4" />
          ) : (
            <AlertCircle className="w-4 h-4" />
          )}
          <span className="text-sm font-medium">
            {integracaoStatus?.status === 'conectado' ? 'Conectado' : 'Integração Pendente'}
          </span>
        </div>
      </div>
      
      {/* Alerta de Integração */}
      {integracaoStatus?.status === 'pendente' && (
        <Card className="border-yellow-300 bg-yellow-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
              <div>
                <p className="font-medium text-yellow-800">Integração com WhatsApp API Pendente</p>
                <p className="text-sm text-yellow-700 mt-1">{integracaoStatus?.mensagem}</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {integracaoStatus?.configuracao_necessaria?.map(item => (
                    <Badge key={item} variant="outline" className="bg-white">{item}</Badge>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="conversas">Conversas</TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
          <TabsTrigger value="config">Configurações</TabsTrigger>
        </TabsList>
        
        {/* DASHBOARD */}
        <TabsContent value="dashboard" className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-100 text-sm">Total Conversas</p>
                    <p className="text-3xl font-bold">{dashboard?.resumo?.total_conversas || 0}</p>
                  </div>
                  <MessageCircle className="w-10 h-10 opacity-50" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100 text-sm">Conversas Hoje</p>
                    <p className="text-3xl font-bold">{dashboard?.resumo?.conversas_hoje || 0}</p>
                  </div>
                  <Clock className="w-10 h-10 opacity-50" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-purple-100 text-sm">Atendidas IA</p>
                    <p className="text-3xl font-bold">{dashboard?.resumo?.atendidas_ia || 0}</p>
                  </div>
                  <Bot className="w-10 h-10 opacity-50" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-amber-100 text-sm">Mensagens Hoje</p>
                    <p className="text-3xl font-bold">{dashboard?.resumo?.mensagens_hoje || 0}</p>
                  </div>
                  <Send className="w-10 h-10 opacity-50" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-red-100 text-sm">Aguardando Humano</p>
                    <p className="text-3xl font-bold">{dashboard?.resumo?.aguardando_humano || 0}</p>
                  </div>
                  <User className="w-10 h-10 opacity-50" />
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Templates Populares */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-500" />
                Templates Mais Usados
              </CardTitle>
            </CardHeader>
            <CardContent>
              {dashboard?.templates_populares?.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Nenhum template cadastrado</p>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-3"
                    onClick={() => { setActiveTab('templates'); setShowNovoTemplate(true); }}
                  >
                    <Plus className="w-4 h-4 mr-1" /> Criar Template
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {dashboard?.templates_populares?.map(template => (
                    <div key={template.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium">{template.nome}</p>
                        <p className="text-sm text-gray-500">{template.categoria}</p>
                      </div>
                      <Badge variant="outline">{template.uso_count} usos</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* CONVERSAS */}
        <TabsContent value="conversas" className="space-y-4">
          <div className="grid md:grid-cols-3 gap-6">
            {/* Lista de Conversas */}
            <Card className="md:col-span-1">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Conversas</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-green-500" />
                  </div>
                ) : conversas.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>Nenhuma conversa</p>
                  </div>
                ) : (
                  <div className="max-h-96 overflow-y-auto">
                    {conversas.map(conv => (
                      <div 
                        key={conv.id}
                        className={`p-4 border-b hover:bg-gray-50 cursor-pointer transition-colors ${
                          conversaSelecionada?.id === conv.id ? 'bg-green-50 border-l-4 border-l-green-500' : ''
                        }`}
                        onClick={() => setConversaSelecionada(conv)}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium">{conv.nome_contato || conv.telefone}</span>
                          <Badge className={STATUS_CONVERSA[conv.status]?.color || 'bg-gray-500'} variant="secondary">
                            {STATUS_CONVERSA[conv.status]?.label}
                          </Badge>
                        </div>
                        {conv.ultima_mensagem && (
                          <p className="text-sm text-gray-500 truncate">
                            {conv.ultima_mensagem.conteudo}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Detalhes da Conversa */}
            <Card className="md:col-span-2">
              <CardContent className="p-0">
                {conversaSelecionada ? (
                  <div className="h-full flex flex-col">
                    {/* Header */}
                    <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white">
                          <User className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="font-medium">{conversaSelecionada.nome_contato || conversaSelecionada.telefone}</p>
                          <p className="text-sm text-gray-500">{conversaSelecionada.telefone}</p>
                        </div>
                      </div>
                      
                      {conversaSelecionada.status === 'ia' && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => transferirParaHumano(conversaSelecionada.id)}
                        >
                          <User className="w-4 h-4 mr-1" />
                          Assumir Atendimento
                        </Button>
                      )}
                    </div>
                    
                    {/* Mensagens */}
                    <div className="flex-1 p-4 space-y-3 max-h-64 overflow-y-auto bg-gray-100">
                      {conversaSelecionada.mensagens?.map(msg => (
                        <div 
                          key={msg.id}
                          className={`flex ${msg.direcao === 'saida' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`max-w-[70%] p-3 rounded-lg ${
                            msg.direcao === 'saida' 
                              ? 'bg-green-500 text-white' 
                              : 'bg-white'
                          }`}>
                            <p className="text-sm">{msg.conteudo}</p>
                            <p className={`text-xs mt-1 ${msg.direcao === 'saida' ? 'text-green-100' : 'text-gray-400'}`}>
                              {new Date(msg.timestamp).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                              {msg.gerado_por === 'ia' && ' • IA'}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Input de Resposta */}
                    <div className="p-4 border-t bg-white">
                      <div className="flex gap-2">
                        <Input 
                          placeholder="Digite sua mensagem..."
                          value={mensagemResposta}
                          onChange={(e) => setMensagemResposta(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && enviarMensagem()}
                        />
                        <Button onClick={enviarMensagem} className="bg-green-500 hover:bg-green-600">
                          <Send className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="h-96 flex items-center justify-center text-gray-400">
                    <div className="text-center">
                      <MessageCircle className="w-16 h-16 mx-auto mb-4 opacity-30" />
                      <p>Selecione uma conversa</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        {/* TEMPLATES */}
        <TabsContent value="templates" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold">Templates de Mensagem</h2>
            <Button onClick={() => setShowNovoTemplate(true)} className="bg-green-500 hover:bg-green-600">
              <Plus className="w-4 h-4 mr-2" />
              Novo Template
            </Button>
          </div>
          
          {templates.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <MessageCircle className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">Nenhum template cadastrado</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {templates.map(template => (
                <Card key={template.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-medium">{template.nome}</p>
                        <Badge variant="outline" className="mt-1">{template.categoria}</Badge>
                      </div>
                      <Badge>{template.uso_count || 0} usos</Badge>
                    </div>
                    <p className="text-sm text-gray-600 mt-3 line-clamp-3">{template.conteudo}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
        
        {/* CONFIGURAÇÕES */}
        <TabsContent value="config" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bot className="w-5 h-5" />
                Configurações da IA
              </CardTitle>
              <CardDescription>Configure o comportamento do chatbot</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {config && (
                <>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">IA Ativa</p>
                      <p className="text-sm text-gray-500">Responder automaticamente às mensagens</p>
                    </div>
                    <Switch 
                      checked={config.ativo}
                      onCheckedChange={(v) => setConfig({...config, ativo: v})}
                    />
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium">Personalidade</label>
                    <Select 
                      value={config.personalidade} 
                      onValueChange={(v) => setConfig({...config, personalidade: v})}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="amigavel">Amigável</SelectItem>
                        <SelectItem value="formal">Formal</SelectItem>
                        <SelectItem value="tecnico">Técnico</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium">Horário Início</label>
                      <Input 
                        type="time"
                        value={config.horario_atendimento_inicio}
                        onChange={(e) => setConfig({...config, horario_atendimento_inicio: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Horário Fim</label>
                      <Input 
                        type="time"
                        value={config.horario_atendimento_fim}
                        onChange={(e) => setConfig({...config, horario_atendimento_fim: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium">Mensagem Fora do Horário</label>
                    <Textarea 
                      value={config.mensagem_fora_horario}
                      onChange={(e) => setConfig({...config, mensagem_fora_horario: e.target.value})}
                      className="mt-1"
                    />
                  </div>
                  
                  <Button onClick={salvarConfig} className="w-full bg-green-500 hover:bg-green-600">
                    Salvar Configurações
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Modal Novo Template */}
      <Dialog open={showNovoTemplate} onOpenChange={setShowNovoTemplate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Novo Template</DialogTitle>
            <DialogDescription>Crie um template de mensagem para uso rápido</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <Input 
              placeholder="Nome do template"
              value={novoTemplate.nome}
              onChange={(e) => setNovoTemplate({...novoTemplate, nome: e.target.value})}
            />
            
            <Select 
              value={novoTemplate.categoria} 
              onValueChange={(v) => setNovoTemplate({...novoTemplate, categoria: v})}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="saudacao">Saudação</SelectItem>
                <SelectItem value="informacao">Informação</SelectItem>
                <SelectItem value="matricula">Matrícula</SelectItem>
                <SelectItem value="financeiro">Financeiro</SelectItem>
                <SelectItem value="outro">Outro</SelectItem>
              </SelectContent>
            </Select>
            
            <Textarea 
              placeholder="Conteúdo da mensagem..."
              value={novoTemplate.conteudo}
              onChange={(e) => setNovoTemplate({...novoTemplate, conteudo: e.target.value})}
              rows={4}
            />
            
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowNovoTemplate(false)} className="flex-1">
                Cancelar
              </Button>
              <Button onClick={criarTemplate} className="flex-1 bg-green-500 hover:bg-green-600">
                Criar Template
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
